import React from "react";
import { useForm, useFormState, useField } from 'react-final-form';
import {
    ArrayInput,
    composeSyncValidators,
    DateTimeInput,
    maxLength,
    maxValue,
    minValue,
    number,
    NumberInput,
    required,
    SimpleFormIterator,
    TextInput,
} from "react-admin";
import FLD from "../model/ISIN";
import YesNo from "../model/YesNo";
import IsinInput from "./IsinInput";
import {
    nominalValueValidation,
    validateNumber_12_6,
    validateSignedNumber_3_6,
    validateNumber_5_6,
    validateNumber_9_6
} from "../validation/numberValidation";
import IsinStatus from "../model/IsinStatus";
import CfiStandard from "../model/CfiStandard";
import { IE11DateInput } from "./IE11DateInput";
import InterestType from "../model/InterestType";
import ConditionalInput from "./ConditionalInput";
import { fillEnums, PermissionInterface } from "../api/commonFunctions";
import DigitalAssetStatus from "../model/DigitalAssetStatus";
import { LeiDataInput, LeiInput } from "./LeiInput";
import { SafeSelectInput } from "./SafeSelectInput";
import InterestFrequency from "../model/InterestFrequency";
import InstrumentCategory from "../model/InstrumentCategory";
import { utcToLocalDateTimeISO } from "../api/dateFormatter";
import { MaturityDate, MaturityType } from "./MaturityDateInput";
import validateIsinStatus from "../validation/validateIsinStatus";
import AutocompleteReferenceInput from "./AutocompleteReferenceInput";
import { Full, Half, Quarter, Row, TwoColumnLayout } from "../layout/LayoutComponents";
import { isValidISIN } from '../validation/isinValidation';
import { computeDigitalAssetFieldPermissions } from './dnaFieldPermissions';
import CallPutType from "../model/CallPut";
import validateLei from "../validation/lei";
import validateDigitalTokenFormat from "../validation/digitalTokenFormat";
import { cfiValidater, fisnValidater } from "../validation/isinFieldValidators";
import {Code, LETTER_TO_CODE} from "../validation/validationRules";

export interface IsinFieldsProps {
    resource: any,
    record: any,
    challenge: boolean
    fieldsToImprove?: any[],
    permissions?: Partial<PermissionInterface>,
}

const DIGITAL_ASSET_TOKENIZATION_LEVEL_CHOICES = [
    { id: "Full", name: "Full" },
    { id: "Partial", name: "Partial" },
];

// Same choices already used to display this field in IsinShow.tsx (ShowDigitalTokenFields) - the raw
// inbound-only sentinel "Y" is deliberately not offered, only the two technical, persisted values.
const DIGITAL_ASSET_STATUS_CHOICES = [
    { id: DigitalAssetStatus.Y_NNA, name: 'Y (NNA)' },
    { id: DigitalAssetStatus.Y_DTIF, name: 'Y (DTIF)' },
];

/**
 * Digital Status input - a selection, not free text (same choices as the Show view).
 * - ROLE_DNA (not admin / not the responsible NNA): the only value that may ever be submitted is
 *   Y_DTIF (see DTI_FIELD_EDIT_PERMISSIONS_ANALYSIS.md §3/§7). Auto-corrected via effect - safe to do
 *   so here because DNA can never touch any other Digital-Asset field, so there is no "change it, then
 *   revert it" scenario that could leave a stale, one-way mutation behind (see note below).
 * - A real NNA (non-XT or XT-NNA) must set Digital Status to exactly Y_NNA when (re-)submitting Ledger
 *   Name/Tokenization Level, and must never newly attest Y_DTIF otherwise - mirrors the inbound rules
 *   (DtiInboundMergePolicy.applyForNnaUpdate / DigitalAssetStatusDeriver). Enforced purely via
 *   `validate` (blocks Save with an error), never by mutating the field - an earlier effect-based
 *   auto-reset did not undo itself when Ledger Name/Tokenization Level were edited and then reverted
 *   back within the same edit, leaving Digital Status wrongly stuck at Y_NNA. Pure validation against
 *   the current, final values has no such state to get stuck: revert the trigger and the violation is
 *   simply gone.
 */
const DigitalAssetStatusInput: React.FC<{
    disabled: boolean;
    lockToYDtif: boolean;
    mustSetToYNna: boolean;
    cannotSetToYDtif: boolean;
}> = ({ disabled, lockToYDtif, mustSetToYNna, cannotSetToYDtif }) => {
    const form = useForm();
    const statusField = useField(FLD.digitalAssetStatus, { subscription: { value: true } });

    React.useEffect(() => {
        if (lockToYDtif && statusField.input.value !== DigitalAssetStatus.Y_DTIF) {
            form.change(FLD.digitalAssetStatus, DigitalAssetStatus.Y_DTIF);
        }
    }, [lockToYDtif, statusField.input.value]);

    const validateStatus = (value: string): string | undefined => {
        if (mustSetToYNna && value !== DigitalAssetStatus.Y_NNA) {
            return 'resources.isin.digitalAssetStatusMustBeNnaWhenOwnedFieldsChanged';
        }
        if (cannotSetToYDtif && value === DigitalAssetStatus.Y_DTIF) {
            return 'resources.isin.digitalAssetStatusMustBeNnaWhenOwnedFieldsChanged';
        }
        return undefined;
    };

    return <SafeSelectInput source={FLD.digitalAssetStatus}
                            allowEmpty={true}
                            choices={DIGITAL_ASSET_STATUS_CHOICES}
                            validate={[validateStatus]}
                            disabled={disabled || lockToYDtif}/>;
};

function splitArrayOnWhitespace(text: string): string[] {
    if (!text) return [];
    return text.split(/\s/)
}

function joinArrayWithNewLines(text: string[]): string {
    if (!text) return "";
    return text.join("\n")
}

const forbidCfi2015InAdditionalInfo = (value?: string) => {
    if (!value) {
        return undefined;
    }

    const text = value.toString().toUpperCase();

    // Block any CFIyyyy pattern, e.g. CFI2015, CFI2001, etc.
    const hasCfiCode = /\bCFI\d{4}\b/.test(text);

    if (hasCfiCode) {
        return "anna.validation.noCfi2015InAdditionalInformationText";
    }

    return undefined;
};

const OldIsinInput = ({ source }: { source: string }) => {
    const { values } = useFormState({ subscription: { values: true } });

    const validateIsin = (value: string): string | undefined => {
        if (!value) {
            return undefined;
        }

        const isinValues = value.trim().split(/\s+/).filter(Boolean);

        if (isinValues.length > 5) {
            return 'resources.isin.oldIsinsInvalid';
        }

        // oldIsin must not contain newIsin
        const newIsin = values?.[FLD.newIsin];
        if (newIsin && isinValues.includes(newIsin)) {
            return 'resources.isin.oldAndNewIsinIdentical';
        }

        // oldIsin must not contain the original ISIN
        const originalIsin = values?.[FLD.id];
        if (originalIsin && isinValues.includes(originalIsin)) {
            return 'resources.isin.isinAlreadyExists';
        }

        for (const isin of isinValues) {
            if (!isValidISIN(isin)) {
                return 'resources.isin.oldIsinInvalid';
            }
        }

        return undefined;
    };

    return <TextInput source={source} validate={validateIsin} style={{ width: '100%' }} />;
};

const NewIsinInput = ({ source }: { source: string }) => {
    const { values } = useFormState({ subscription: { values: true } });

    const validateIsin = (value: string): string | undefined => {
        if (!value) {
            return undefined;
        }

        // newIsin must not equal any oldIsin entry
        const oldIsinsRaw = values?.[FLD.oldIsin];
        if (oldIsinsRaw) {
            const oldIsins = oldIsinsRaw.trim().split(/\s+/).filter(Boolean);
            if (oldIsins.includes(value)) {
                return 'resources.isin.oldAndNewIsinIdentical';
            }
        }

        // newIsin must not equal the original ISIN
        const originalIsin = values?.[FLD.id];
        if (originalIsin && value === originalIsin) {
            return 'resources.isin.isinAlreadyExists';
        }

        if (!isValidISIN(value)) {
            return 'resources.isin.oldIsinInvalid';
        }

        return undefined;
    };

    return <TextInput source={source} validate={validateIsin} style={{ width: '100%' }} />;
};

const ISO2019 = "CFI2015";
const ISO2001 = "CFI2001";

/**
 * Read-only input that derives the instrument category from the first character of the CFI code.
 * - Writes the enum CODE (e.g. "EQ") to form state
 * - Displays the human label (e.g. "Equities") via `format`
 */
const InstrumentCategoryDerivedInput: React.FC = () => {
    const form = useForm();

    const cfiField = useField(FLD.cfiCode, { subscription: { value: true } });
    const icField  = useField(FLD.instrumentCategory, { subscription: { value: true } });

    // Normalize the CFI value (string, uppercase, trimmed) and read the first character.
    const cfi = (cfiField.input.value ?? "").toString().toUpperCase().trim();
    const first = cfi.charAt(0);

    // Compute the derived enum CODE. Fallback to "OT" if not in the mapping.
    const derivedCode: Code = (LETTER_TO_CODE[first] ?? "OT") as Code;

    React.useEffect(() => {
        if (icField.input.value !== derivedCode) {
            form.change(FLD.instrumentCategory, derivedCode);
        }
    }, [derivedCode, icField.input.value]);

    // Presentational formatter: converts the stored enum CODE (e.g. "EQ" into the human-readable label (e.g. "Equities") for display.
    const formatToLabel = (code?: string) =>
            (code && InstrumentCategory[code as Code]) || code || "";

    return (
            <TextInput
                    source={FLD.instrumentCategory}
                    disabled
                    fullWidth
                    helperText="Derived from CFI code"
                    format={formatToLabel}
                    parse={(v) => v}
            />
    );
};

// The 3 DTI-controlled fields (see dnaFieldPermissions.ts) - editing only these must not force an
// isinStatus transition away from NEW, since ROLE_DNA is not permitted to change isinStatus at all.
const DTI_CONTROLLED_FIELDS = [
    FLD.digitalAssetStatus,
    FLD.equivalentDigitalTokenGroup,
    FLD.digitalTokenIdentifiers
];

const IsinFields = ({ resource, record, fieldsToImprove, challenge, permissions }: IsinFieldsProps) => {
    const isIsinResource = resource === 'isin';

    const creating = !record?.dateAdd;

    const { values, dirtyFields } = useFormState({
        subscription: { values: true, dirtyFields: true },
    });

    // True as soon as ANY dirty field is outside the 3 DTI-controlled fields (array fields register
    // as e.g. "digitalTokenIdentifiers[0]" in dirtyFields, hence the prefix check). Used to allow a
    // NEW ISIN to stay NEW when only DTI info is being added/updated - see validateStatus below.
    const onlyDtiFieldsChanged = Object.keys(dirtyFields)
        .filter(key => dirtyFields[key])
        .every(key => DTI_CONTROLLED_FIELDS.some(f => key === f || key.startsWith(`${f}[`)));

    // Role- and jurisdiction-based editability of the 5 Digital-Asset-/DTI fields (see
    // DTI_FIELD_EDIT_PERMISSIONS_ANALYSIS.md §7/§8/§12). Server-side enforcement is authoritative;
    // this only controls the GUI presentation (editable vs. read-only/disabled).
    const currentIsin: string = values?.[FLD.id] ?? record?.[FLD.id];

    // The persisted Digital Status (before this edit) - see computeDigitalAssetFieldPermissions.
    const persistedDigitalAssetStatus: string = record?.[FLD.digitalAssetStatus];

    // Whether Distributed Ledger Name / Digital Asset Tokenization Level's CURRENT (final) value differs
    // from the PERSISTED record - a plain value comparison, deliberately NOT based on `dirtyFields`
    // (which tracks "was this field ever touched during the edit", not "does it end up different"). Using
    // dirtyFields previously caused a bug: editing Ledger Name auto-reset Digital Status to Y_NNA, but
    // reverting Ledger Name back to its original value left Digital Status wrongly stuck at Y_NNA,
    // because dirtyFields momentarily having been true doesn't undo an already-applied side effect. A
    // plain final-value comparison has no such history to get stuck on.
    const nnaOwnedFieldsChanged =
        (values?.[FLD.digitalDistLedgerName] ?? '') !== (record?.[FLD.digitalDistLedgerName] ?? '')
        || (values?.[FLD.digitalAssetTokenizationLevel] ?? '') !== (record?.[FLD.digitalAssetTokenizationLevel] ?? '');

    const {
        canEditNnaControlledFields,
        canEditDtiControlledFields,
        dnaLocksDigitalStatusToYDtif,
        nnaMustSetStatusToYNna,
        nnaCannotSetStatusToYDtif,
        generalFieldsDisabled
    } = computeDigitalAssetFieldPermissions(currentIsin, permissions, persistedDigitalAssetStatus, nnaOwnedFieldsChanged);

    const isDeleted = values?.[FLD.isinStatus] === "D";
    const isNewAndCreating = values?.[FLD.isinStatus] === "N" && creating;
    const allCfiChoices = fillEnums(CfiStandard, false);

    const currentCfi = values?.[FLD.cfiVersion];
    const originalCfi = record?.[FLD.cfiVersion];

    const isActive = !isDeleted;

    const cfiVersionChoices =
            // Create → only 2019
            isNewAndCreating
                    ? allCfiChoices.filter(c => c.id === "CFI2015")
                    // Challenge:
                    : challenge
                            // If original was 2001 → show both so user can upgrade to 2019
                            ? (originalCfi === "CFI2001"
                                    ? allCfiChoices.filter(c => c.id === "CFI2015" || c.id === "CFI2001")
                                    // Otherwise only 2019; no downgrade path
                                    : allCfiChoices.filter(c => c.id === "CFI2015"))
                            // Normal modify:
                            : isDeleted
                                    ? allCfiChoices.filter(c => c.id === "CFI2015" || c.id === "CFI2001")
                                    : (originalCfi === "CFI2001"
                                            ? allCfiChoices.filter(c => c.id === "CFI2015" || c.id === "CFI2001")
                                            : allCfiChoices.filter(c => c.id === "CFI2015"));

    const CfiVersionValidator = (value: string) => {
        // Challenge rules
        if (challenge) {
            // If original was 2019 and user tries to downgrade → specific alert text
            if (originalCfi === "CFI2015" && value === "CFI2001") {
                return "CFI version may only be challenged to update from older to newer.";
            }
            // In challenge, only 2019 should be accepted
            if (value !== "CFI2015") {
                return "In challenge mode, CFI Version must be ISO 10962:2019.";
            }
        }

        // Create must be 2019 (you also disable + default this below)
        if (isNewAndCreating && value !== "CFI2015") {
            return "CFI needs to be in Version2019";
        }

        // Active ISINs must end up 2019
        if (isActive && value !== "CFI2015") {
            return "CFI needs to be in Version2019";
        }

        // Inactive (Deleted) may keep 2001
        return undefined;
    };




// usage inside your IsinFields

    function validateStatus(value: string) {
        return validateIsinStatus(
            value,
            !!record.dateAdd,  // is filled only by the server
            onlyDtiFieldsChanged
        );
    }

    return <TwoColumnLayout resource={resource} dirtyfields={dirtyFields} fieldstoimprove={fieldsToImprove}>
        <fieldset disabled={generalFieldsDisabled} style={{ border: 'none', margin: 0, padding: 0 }}>
        <Row>
            <Half>
                {resource === 'isin' ? <IsinInput source={FLD.id}/> : null}
            </Half>
            <Half>
                <InstrumentCategoryDerivedInput />
            </Half>
        </Row>
        <Row>
            <Half>
                <OldIsinInput source={FLD.oldIsin}/>
            </Half>
            <Half>
                <NewIsinInput source={FLD.newIsin}/>
            </Half>
        </Row>
        <Row>
            <Full>
                <ConditionalInput source={FLD.issuerNameLong}>
                    <LeiDataInput maxLength="400"/>
                </ConditionalInput>
            </Full>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.issuerLegalRegCountry}>
                    <AutocompleteReferenceInput source={FLD.issuerLegalRegCountry}
                                                reference="countries"/>
                </ConditionalInput>
            </Half>

        </Row>
        <Row>
            <Full>
                {/* additional Entity Profile Data */}
                <ConditionalInput source={FLD.issueDescription}>
                    <TextInput validate={maxLength(120)}/>
                </ConditionalInput>
            </Full>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.issueCurrency}>
                    <AutocompleteReferenceInput reference="currencies"/>
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.isinStatus}>
                    <SafeSelectInput choices={fillEnums(IsinStatus)}
                                     validate={isIsinResource ? [validateStatus] : []}/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.cfiCode}>
                    <TextInput allowEmpty={!isIsinResource} validate={!challenge ? cfiValidater : null} parse={value => value ? value.toUpperCase() : ''}/>
                </ConditionalInput>
            </Half>
            <Half>
                <SafeSelectInput
                        source={FLD.cfiVersion}
                        choices={cfiVersionChoices}
                        validate={[CfiVersionValidator]}
                />
            </Half>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.fisn}>
                    <TextInput allowEmpty={!isIsinResource} validate={!challenge ? fisnValidater : null}/>
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.preliminaryTerms}>
                    <SafeSelectInput allowEmpty={true}
                                     choices={fillEnums(YesNo)}/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.smallestDenomination}>
                    <TextInput validate={validateNumber_12_6}/>
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.nominalValue}>
                    <TextInput validate={nominalValueValidation}/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.maturityDateCode}>
                    <MaturityType/>
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.maturityDate}>
                    <MaturityDate/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.interestType}>
                    <SafeSelectInput allowEmpty={true}
                                     choices={fillEnums(InterestType)}
                    />
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.interestRate}>
                    <TextInput validate={validateSignedNumber_3_6}/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.interestFrequency}>
                    <SafeSelectInput allowEmpty={true}
                                     choices={fillEnums(InterestFrequency)}/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Quarter>
                <ConditionalInput source={FLD.interestPayMonth}>
                    <NumberInput min={1} max={12}
                                 validate={composeSyncValidators(number(), minValue(1), maxValue(12))}/>
                </ConditionalInput>
            </Quarter>
            <Quarter>
                <ConditionalInput source={FLD.interestPayDay}>
                    <NumberInput min={1} max={31}
                                 validate={composeSyncValidators(number(), minValue(1), maxValue(31))}/>
                </ConditionalInput>
            </Quarter>
            <Half>
                <ConditionalInput source={FLD.interestFirstPayDate}>
                    <IE11DateInput/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.callPut}>
                    <SafeSelectInput allowEmpty={true}
                                     choices={fillEnums(CallPutType)}
                    />
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.conversionRatio}>
                    <TextInput validate={validateNumber_5_6}/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.exercisePrice}>
                    <TextInput validate={validateNumber_9_6}/>
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.exercisePriceCurrency}>
                    <AutocompleteReferenceInput reference="currencies"/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.underlyings}>
                    <TextInput multiline
                               rows={3}
                               format={joinArrayWithNewLines}
                               parse={splitArrayOnWhitespace}/>
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.underlyingInstrumentName}>
                    <TextInput multiline rows={3} validate={maxLength(255)}/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Full>
                <TextInput source={FLD.additionalInformationText} validate={forbidCfi2015InAdditionalInfo}/>
            </Full>
        </Row>
        <Row>
            <Half>
                <TextInput source={FLD.marketIdCodes}
                           multiline
                           rows={3}
                           format={joinArrayWithNewLines}
                           parse={splitArrayOnWhitespace}/>
            </Half>

        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.leadManagerLEI}>
                    <LeiInput dependentFields={[
                        [FLD.leadManagerName, "legalName.name"]
                    ]}/>
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.leadManagerName}>
                    <LeiDataInput/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.fundManagerLEI}>
                    <LeiInput dependentFields={[
                        [FLD.fundManagerName, "legalName.name"]
                    ]}/>
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.fundManagerName}>
                    <LeiDataInput/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Full>
                <ConditionalInput source={FLD.centralSecDepsAndLEIs}>
                    <ArrayInput>
                        <SimpleFormIterator>
                            <LeiInput source={FLD.csdLei}
                                      dependentFields={[
                                          [FLD.csdName, "legalName.name"]
                                      ]}/>
                            <LeiDataInput source={FLD.csdName}/>

                        </SimpleFormIterator>
                    </ArrayInput>
                </ConditionalInput>
            </Full>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.issuerLEI}>
                    <LeiInput source={FLD.issuerLEI}
                              dependentFields={[
                                  [FLD.issuerHQCountry, "headquartersAddress.country"],
                                  [FLD.issuerHQAddress1, "headquartersAddress.addressLines[0]"],
                                  [FLD.issuerHQAddress2, "headquartersAddress.addressLines[1]"],
                                  [FLD.issuerHQStateProvince, "headquartersAddress.region"],
                                  [FLD.issuerHQPostCode, "headquartersAddress.postalCode"],
                                  [FLD.issuerHQCity, "headquartersAddress.city"],

                                  [FLD.issuerLegalRegAddress1, "legalAddress.addressLines[0]"],
                                  [FLD.issuerLegalRegAddress2, "legalAddress.addressLines[1]"],
                                  [FLD.issuerLegalRegStateProvince, "legalAddress.region"],
                                  [FLD.issuerLegalRegPostCode, "legalAddress.postalCode"],
                                  [FLD.issuerLegalRegCity, "legalAddress.city"],

                                  // questionable
                                  [FLD.issuerLegalRegCountry, "legalAddress.country"],
                                  [FLD.issuerNameLong, "legalName.name"],
                              ]}/>
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.issuerSupranational}>
                    <SafeSelectInput allowEmpty={true}
                                     choices={fillEnums(YesNo)}/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.issuerNameShort}>
                    <TextInput/>
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.issuerHQCountry}>
                    <AutocompleteReferenceInput reference="countries"/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Half>
                <TextInput source={FLD.headOfficeLEI} validate={validateLei}/>
            </Half>
            <Half>
                <TextInput source={FLD.headOfficeName} validate={maxLength(255)}/>
            </Half>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.issuerHQAddress1}>
                    <LeiDataInput/>
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.issuerLegalRegAddress1}>
                    <LeiDataInput/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.issuerHQAddress2}>
                    <LeiDataInput/>
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.issuerLegalRegAddress2}>
                    <LeiDataInput/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.issuerHQStateProvince}>
                    <LeiDataInput/>
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.issuerLegalRegStateProvince}>
                    <LeiDataInput/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.issuerHQPostCode}>
                    <LeiDataInput/>
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.issuerLegalRegPostCode}>
                    <LeiDataInput/>
                </ConditionalInput>
            </Half>
        </Row>
        <Row>
            <Half>
                <ConditionalInput source={FLD.issuerHQCity}>
                    <LeiDataInput/>
                </ConditionalInput>
            </Half>
            <Half>
                <ConditionalInput source={FLD.issuerLegalRegCity}>
                    <LeiDataInput/>
                </ConditionalInput>
            </Half>
        </Row>
        </fieldset>
        {isIsinResource && <Row>
            <Half>
                <DigitalAssetStatusInput disabled={!canEditNnaControlledFields && !dnaLocksDigitalStatusToYDtif}
                                         lockToYDtif={dnaLocksDigitalStatusToYDtif}
                                         mustSetToYNna={nnaMustSetStatusToYNna}
                                         cannotSetToYDtif={nnaCannotSetStatusToYDtif}/>
            </Half>
            <Half>
                <TextInput source={FLD.digitalDistLedgerName}
                           disabled={!canEditNnaControlledFields}
                           validate={maxLength(120)}/>
            </Half>
        </Row> || <></>}
        {isIsinResource && <Row>
            <Half>
                <SafeSelectInput source={FLD.digitalAssetTokenizationLevel}
                                 allowEmpty={true}
                                 disabled={!canEditNnaControlledFields}
                                 choices={DIGITAL_ASSET_TOKENIZATION_LEVEL_CHOICES}/>
            </Half>
            <Half>
                <TextInput source={FLD.equivalentDigitalTokenGroup}
                           disabled={!canEditDtiControlledFields}
                           validate={validateDigitalTokenFormat}/>
            </Half>
        </Row> || <></>}
        {isIsinResource && <Row>
            <Full>
                <ArrayInput source={FLD.digitalTokenIdentifiers} disabled={!canEditDtiControlledFields}>
                    <SimpleFormIterator disableReordering>
                        <TextInput source="" validate={validateDigitalTokenFormat}/>
                    </SimpleFormIterator>
                </ArrayInput>
            </Full>
        </Row> || <></>}
        {isIsinResource && <Row>
            <Quarter>
                <DateTimeInput source={FLD.dateAdd}
                               format={utcToLocalDateTimeISO}
                               variant='standard'
                               disabled/>
            </Quarter>
            <Quarter>
                <TextInput source={FLD.userIdAdd}
                           variant='standard'
                           disabled/>
            </Quarter>
            <Quarter>
                <DateTimeInput source={FLD.dateModify}
                               format={utcToLocalDateTimeISO}
                               variant='standard'
                               disabled/>
            </Quarter>
            <Quarter>
                <TextInput source={FLD.userIdModify}
                           variant='standard'
                           disabled/>
            </Quarter>
        </Row> || <></>}
    </TwoColumnLayout>
};

export default IsinFields;
